import "@src/components/compliance/compliance.css";
import "./appraisals.css";
import { Modal, Table, Tag } from "antd";
import { useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { stopAppraisalScheduleMutation } from "@src/util/mutation/appraisal.mutation";
import { describeRepeat, formatDate } from "./appraisalHelpers";

/**
 * Appraisals set up to open later and/or repeat - one employee's (on their Appraisals tab) or
 * everyone's (the Appraisals page's Scheduled tab) - with Stop.
 */
export const AppraisalSchedules = ({ schedules = [], loading = false, showEmployee = false, readOnly = false, onChanged = () => {}, onOpenEmployee }) => {
    const dispatch = useDispatch();
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const canModify = !readOnly && authenticatedUserDetail?.permission?.Appraisals?.modify !== false;
    const [stopSchedule] = useMutation(stopAppraisalScheduleMutation);

    const stop = (row) => Modal.confirm({
        title: row.everyMonths ? `Stop repeating ${row.title}?` : `Cancel the scheduled ${row.title}?`,
        content: row.everyMonths
            ? `No more ${row.title} appraisals will open for ${row.employeeName}. Any that are already open carry on.`
            : `It won't open on ${formatDate(row.nextOpenOn)}.`,
        okText: row.everyMonths ? "Stop repeating" : "Cancel it",
        okButtonProps: { danger: true },
        cancelText: "Keep it",
        onOk: async () => {
            try {
                await stopSchedule({ variables: { id: row._id } });
                dispatch(setNotification({ error: false, notificationState: true, message: row.everyMonths ? "It won't repeat any more" : "Scheduled appraisal cancelled" }));
                onChanged();
            } catch (err) {
                dispatch(setNotification({ error: true, notificationState: true, message: err.message }));
            }
        },
    });

    const columns = [
        showEmployee && {
            title: "Employee", dataIndex: "employeeName",
            render: (v, row) => (onOpenEmployee ? <button className="cmp-link" onClick={() => onOpenEmployee(row.employeeId)}>{v}</button> : v),
        },
        {
            title: "Appraisal", dataIndex: "title",
            render: (v, row) => (
                <div className="cmp-person">
                    <span className="cmp-person-name">{v}</span>
                    <span className="cmp-muted">{row.templateName}{row.templateArchived && <Tag color="red" style={{ marginLeft: 6 }}>Template archived - waiting</Tag>}</span>
                </div>
            ),
        },
        { title: "How often", dataIndex: "everyMonths", render: (v) => describeRepeat(v) },
        { title: "Set up", dataIndex: "createdAt", render: (v, row) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{row.createdByName || "--"}</span></div> },
        { title: "Next opens", dataIndex: "nextOpenOn", render: (v, row) => <span>{formatDate(v)}<span className="cmp-muted"> · {row.completeWithinDays} days to complete</span></span> },
        { title: "Reviewer", dataIndex: "reviewer", render: (v, row) => (row.reviewerIsRole ? <span>{v} <span className="cmp-muted">(role)</span></span> : v || <span className="cmp-overdue">Has left</span>) },
        canModify && {
            title: "", dataIndex: "stop", align: "right",
            render: (_, row) => <button className="sm-btn" onClick={() => stop(row)}>{row.everyMonths ? "Stop repeating" : "Cancel"}</button>,
        },
    ].filter(Boolean);

    return (
        <Table
            className="curvedTable"
            loading={loading}
            columns={columns}
            dataSource={schedules.map((row) => ({ ...row, key: row._id }))}
            pagination={{ pageSize: 25, hideOnSinglePage: true }}
            locale={{ emptyText: "Nothing scheduled. When you start an appraisal you can set it to open later or to repeat." }}
        />
    );
};
